import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_LOCALE,
  LOCALE_LIST,
  LOCALE_STORAGE_KEY,
  LOCALES,
  resolveLocale,
} from './locales';
import ptBR from './dictionaries/pt-BR';
import en from './dictionaries/en';
import { vipPt, vipEn } from './dictionaries/vip';
import { accountPt, accountEn } from './dictionaries/account';
import { termsPt, termsEn } from './dictionaries/terms';

const dictionaries = {
  'pt-BR': { ...ptBR, vip: vipPt, account: accountPt, terms: termsPt },
  en: { ...en, vip: vipEn, account: accountEn, terms: termsEn },
};

const I18nContext = createContext(null);

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length; i += 1) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[parts[i]];
  }
  return cur;
}

function interpolate(template, vars) {
  if (!vars || typeof template !== 'string') return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = vars[key];
    return value == null ? '' : String(value);
  });
}

function readStoredLocale() {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) return resolveLocale(stored);
  } catch {
    /* ignore */
  }
  return DEFAULT_LOCALE;
}

function applyDocumentLang(locale) {
  const meta = LOCALES[locale] || LOCALES[DEFAULT_LOCALE];
  if (typeof document !== 'undefined') {
    document.documentElement.lang = meta.htmlLang;
  }
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    const next = readStoredLocale();
    applyDocumentLang(next);
    return next;
  });

  const setLocale = useCallback((nextCode) => {
    const next = resolveLocale(nextCode);
    setLocaleState(next);
    applyDocumentLang(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const get = useCallback(
    (key) => {
      const primary = getByPath(dictionaries[locale], key);
      if (primary !== undefined) return primary;
      return getByPath(dictionaries[DEFAULT_LOCALE], key);
    },
    [locale]
  );

  const t = useCallback(
    (key, vars) => {
      const primary = get(key);
      if (typeof primary === 'string') return interpolate(primary, vars);
      if (typeof primary === 'number') return String(primary);
      return key;
    },
    [get]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      get,
      locales: LOCALE_LIST,
      localeMeta: LOCALES[locale] || LOCALES[DEFAULT_LOCALE],
    }),
    [locale, setLocale, t, get]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}

export function useT() {
  return useI18n().t;
}

export default I18nContext;
